import {
  ButtonIcon,
  ConfirmationDialog,
  Grid,
} from '@statsbomb/kitbag-components';
import { _VideoPlayer } from '@statsbomb/video-client-js';
import { useAtomValue, useSetAtom } from 'jotai';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { messageReviewLibraryEntryActionAtom } from '../../atoms/messages';
import { editingOffsetsAtom, editingSourceDataAtom } from '../../atoms/review';
import { selectedSourceDataAtom } from '../../atoms/selectedSourceData';
import useApi, { API_ENDPOINTS } from '../../hooks/api';
import useVideoListings from '../../hooks/videoListings';
import { auditDataPropTypes } from '../../serialisers/auditData';
import { qaDataPropTypes } from '../../serialisers/qualityAsssurance';
import { sourceSerialiser } from '../../serialisers/sourceData';
import { microsecondsToTimeStamp } from '../../utils/time';
import OffsetData from '../OffsetData';
import { VALUE_IDENTIFYING_NOT_SHOWN } from '../OffsetData/OffsetData';
import QAChecklist from '../QAChecklist';
import { QAInfoView } from '../QAInfoView/QAInfoView';
import { SourceData } from '../SourceData/SourceData';
import { FOOTBALL_IDENTIFIER } from '../Sport/Sport';
import { TechnicalMetadataViewSingle } from '../TechnicalMetadataViewSingle/TechnicalMetadataViewSingle';
import TechnicalMetadataWarnings from '../TechnicalMetadataWarnings';
import { VideoAdditionalSettings } from '../VideoAdditionalSettings/VideoAdditionalSettings';
import { ReviewLibraryEntryMessages } from './ReviewLibraryEntryMessages/ReviewLibraryEntryMessages';
import { MEDIA_SOURCE } from '../../constants/source';
import MisalignmentStatusTable from '../MisalignmentStatusTable';
import AlignmentStatusTable from '../AlignmentStatusTable';
import { ModalContent, Container } from './ReviewLibraryEntry.styles';

export function ReviewLibraryEntry({
  auditData,
  alignmentStatus,
  misalignmentIssue,
  misalignmentStatus,
  gslCredentialsRequired,
  isDoNotUse,
  matchId,
  metadata,
  part,
  periodStart: initialPeriodStart,
  periodEnd: initialPeriodEnd,
  qualityAssuranceInfo,
  size,
  sourceData,
  sport,
  track,
  url,
  version,
}) {
  const intl = useIntl();
  const api = useApi();

  const [src, setSrc] = useState();

  const [periodStartNotShown, setPeriodStartNotShown] = useState(false);
  const [periodEndNotShown, setPeriodEndNotShown] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const [displayModal, setDisplayModal] = useState(false);

  const setReviewLibraryEntryMessage = useSetAtom(
    messageReviewLibraryEntryActionAtom,
  );

  const selectedSourceData = useAtomValue(selectedSourceDataAtom);
  const setIsEditingSourceData = useSetAtom(editingSourceDataAtom);
  const setIsEditingOffset = useSetAtom(editingOffsetsAtom);

  const { refreshVideoSearchResults } = useVideoListings();

  const isGSL = sourceData?.sourceName === MEDIA_SOURCE.GSL;

  const resetOffsetSelections = () => {
    setPeriodStart(
      initialPeriodStart && initialPeriodStart > 0
        ? microsecondsToTimeStamp(initialPeriodStart, {
            includeDecimals: true,
          })
        : '',
    );
    setPeriodStartNotShown(
      initialPeriodStart && initialPeriodStart === VALUE_IDENTIFYING_NOT_SHOWN,
    );
    setPeriodEnd(
      initialPeriodEnd && initialPeriodEnd > 0
        ? microsecondsToTimeStamp(initialPeriodEnd, { includeDecimals: true })
        : '',
    );
    setPeriodEndNotShown(
      initialPeriodEnd && initialPeriodEnd === VALUE_IDENTIFYING_NOT_SHOWN,
    );
  };

  const clearSelectionsAndMessages = () => {
    setReviewLibraryEntryMessage();
    resetOffsetSelections();
  };
  useEffect(() => {
    clearSelectionsAndMessages();
  }, [displayModal]);

  const videoReference = {
    matchId,
    matchPart: part,
    sport,
    track,
    version,
  };

  const uniqueIdentifier = JSON.stringify(videoReference);
  useEffect(() => {
    setSrc(url);
  }, [uniqueIdentifier]);

  const handleReviewClick = () => setDisplayModal(true);

  const handleCancel = () => {
    setIsEditingSourceData(false);
    setIsEditingOffset(false);
    setDisplayModal(false);
  };

  const title = intl.formatMessage({
    defaultMessage: 'Review Video',
    id: '29788',
  });

  const SOURCE_DATA_SAVE_SUCCESS_MESSAGE = intl.formatMessage({
    defaultMessage: 'Source data updated',
    id: '00cb1',
  });
  const SOURCE_DATA_SAVE_FAILURE_MESSAGE = intl.formatMessage({
    defaultMessage: 'Source data failed to update. Please try again',
    id: '4d727',
  });
  const UNKNOWN = intl.formatMessage({
    defaultMessage: 'N/A',
    id: '382b0',
  });

  const {
    doNotUseLastUpdatedBy,
    offsetsLastUpdatedBy,
    sourceDataLastUpdatedBy,
  } = auditData || {};

  const getDisplayValue = (user) =>
    user?.emailAddress || user?.userId || UNKNOWN;

  const isFootballVideo = sport === FOOTBALL_IDENTIFIER;

  const onSourceDataSave = async () => {
    const response = await api.put(
      `${API_ENDPOINTS.videoService}/${sport}/${matchId}/${part}/${track}/${version}/library-entry`,
      {
        data: JSON.stringify({
          ...sourceSerialiser(selectedSourceData),
        }),
      },
    );
    if (response?.data) {
      await refreshVideoSearchResults();
      setIsEditingSourceData(false);
      setReviewLibraryEntryMessage({ text: SOURCE_DATA_SAVE_SUCCESS_MESSAGE });
    } else {
      setReviewLibraryEntryMessage({
        text: SOURCE_DATA_SAVE_FAILURE_MESSAGE,
        type: 'error',
      });
    }
  };

  const onSave = async () => {
    await refreshVideoSearchResults(sport, matchId);
    setReviewLibraryEntryMessage({
      text: intl.formatMessage({
        defaultMessage: 'Video information updated',
        id: 'cf1c6',
      }),
    });
  };

  return (
    <>
      <ButtonIcon
        icon="Info"
        onClick={handleReviewClick}
        size={size}
        title={title}
        variant="secondary"
      />
      {displayModal && (
        <ConfirmationDialog
          cancelLabel={null}
          confirmLabel={intl.formatMessage({
            defaultMessage: 'Close',
            id: 'd3d2e',
          })}
          id={`videoaction.review.${uniqueIdentifier}`}
          isOpen={displayModal}
          onCancel={handleCancel}
          onConfirm={handleCancel}
          title={title}
        >
          {displayModal && (
            <>
              <TechnicalMetadataWarnings />
              <ReviewLibraryEntryMessages />
              <ModalContent>
                <Grid>
                  <Grid item xs={12} xl={6}>
                    <Container>
                      {/* Disabled as known prefix for dev only internal components */}
                      {/* eslint-disable-next-line react/jsx-pascal-case */}
                      <_VideoPlayer
                        autoPlay
                        gslCredentialsRequired={gslCredentialsRequired}
                        src={src}
                        testId={`preview-${src}`}
                      />
                    </Container>
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <Grid>
                      <Grid item xs={12} xl={isGSL ? 12 : 6}>
                        <Container>
                          <QAChecklist videoReference={videoReference} />
                        </Container>
                        {!isGSL && (
                          <Container>
                            <SourceData
                              editable
                              lastUpdatedBy={getDisplayValue(
                                sourceDataLastUpdatedBy,
                              )}
                              sourceData={sourceData}
                              sport={sport}
                              onSave={onSourceDataSave}
                            />
                          </Container>
                        )}
                        {isFootballVideo && (
                          <Container>
                            <OffsetData
                              editingIsDisabled={isGSL}
                              lastUpdatedBy={getDisplayValue(
                                offsetsLastUpdatedBy,
                              )}
                              periodStartNotShown={periodStartNotShown}
                              periodStart={periodStart}
                              periodEnd={periodEnd}
                              periodEndNotShown={periodEndNotShown}
                              reset={resetOffsetSelections}
                              setPeriodStartNotShown={setPeriodStartNotShown}
                              setPeriodEndNotShown={setPeriodEndNotShown}
                              setPeriodEnd={setPeriodEnd}
                              setPeriodStart={setPeriodStart}
                              videoReference={videoReference}
                              onSave={onSave}
                            />
                          </Container>
                        )}
                        {isGSL && (
                          <Container>
                            <AlignmentStatusTable
                              lastUpdatedBy={
                                auditData?.alignmentLastUpdatedBy?.emailAddress
                              }
                              alignmentStatus={alignmentStatus}
                            />
                          </Container>
                        )}
                        {isGSL && (
                          <Container>
                            <MisalignmentStatusTable
                              lastUpdatedBy={
                                auditData?.alignmentStatusLastUpdatedBy
                                  ?.emailAddress
                              }
                              misalignmentIssue={misalignmentIssue}
                              misalignmentStatus={misalignmentStatus}
                            />
                          </Container>
                        )}
                        <Container>
                          <VideoAdditionalSettings
                            lastUpdatedBy={getDisplayValue(
                              doNotUseLastUpdatedBy,
                            )}
                            videoReference={videoReference}
                            isDoNotUse={isDoNotUse}
                          />
                        </Container>
                      </Grid>
                      {!isGSL && (
                        <Grid item xs={12} xl={6}>
                          {metadata && (
                            <Container>
                              <TechnicalMetadataViewSingle
                                metadata={metadata}
                              />
                            </Container>
                          )}
                          {!gslCredentialsRequired && (
                            <Container>
                              <QAInfoView
                                qualityAssuranceInfo={qualityAssuranceInfo}
                              />
                            </Container>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </ModalContent>
            </>
          )}
        </ConfirmationDialog>
      )}
    </>
  );
}

ReviewLibraryEntry.propTypes = {
  alignmentStatus: PropTypes.string.isRequired,
  auditData: PropTypes.shape(auditDataPropTypes).isRequired,
  gslCredentialsRequired: PropTypes.bool,
  isDoNotUse: PropTypes.bool.isRequired,
  matchId: PropTypes.string,
  metadata: PropTypes.shape().isRequired,
  misalignmentIssue: PropTypes.oneOf([String, null]),
  misalignmentStatus: PropTypes.oneOf([String, null]),
  part: PropTypes.string,
  periodEnd: PropTypes.number,
  periodStart: PropTypes.number,
  qualityAssuranceInfo: PropTypes.shape(qaDataPropTypes).isRequired,
  size: PropTypes.string,
  sourceData: PropTypes.shape({
    isLicensed: PropTypes.string,
    sourceName: PropTypes.string,
    sourceType: PropTypes.string,
    uploadMethod: PropTypes.string,
  }).isRequired,
  sport: PropTypes.string,
  track: PropTypes.string,
  url: PropTypes.string,
  version: PropTypes.string,
};

ReviewLibraryEntry.defaultProps = {
  gslCredentialsRequired: false,
  matchId: undefined,
  misalignmentIssue: undefined,
  misalignmentStatus: undefined,
  part: undefined,
  periodEnd: undefined,
  periodStart: undefined,
  size: undefined,
  sport: undefined,
  track: undefined,
  url: undefined,
  version: undefined,
};

export default { ReviewLibraryEntry };
